// @ts-strict-ignore
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';
import { BufferAttribute, Color, Matrix4, Mesh, MeshBasicMaterial, MeshPhongMaterial, RepeatWrapping, Shape, ShapeGeometry, Texture, Vector3 } from 'three';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TextureCacheManager } from '@project/components/article/manager/texture-cache.manager';
import { CommonConstants } from '@3d-scene/constants/common-constants';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { FaceUtils } from '@3d-scene/common/util/face-utils';
import { WxText } from './shape/wx-text';
import { WxDrawingElement } from './wx-drawing-element';
import { ArticlesCreator } from '@project/components/article/presenter/article-creator';
import { MetaElementUsageIdentityVO, SimpleTextValueVO, TextureAreaUsageVO } from '@shared/vo/meta-element.vo';
import { LabelTextureDataVO, LabelTextureDescription } from '@3d-scene/processor/texture.vo';
import { ArrayUtils } from '@shared/util/array-utils';
import { TextureUtils } from '@3d-scene/model3d/util/texture-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { Injectable } from '@angular/core';
import { StringUtils } from '@shared/util/string-utils';
import { HtmlUtils } from '@shared/util/html-utils';

@Injectable({ providedIn: 'root' })
export class ExportToolUtils {


  constructor(private textureCacheManager: TextureCacheManager) {
  }

  public static translateToConfiguratorCoords(x: number, y: number, z: number): Vector3 {
    return new Vector3(-CommonConstants.COMMON_SCALE * y, CommonConstants.COMMON_SCALE * z, -CommonConstants.COMMON_SCALE * x);
  }

  public static translateToConfiguratorTransformation(x: number, y: number, z: number, rotX: number, rotY: number, rotZ: number): Matrix4 {
    const matrix: Matrix4 = ExportToolUtils.translateToConfiguratorRotationMatrix(rotX, rotY, rotZ);
    const position: Vector3 = ExportToolUtils.translateToConfiguratorCoords(x, y, z);
    matrix.setPosition(position.x, position.y, position.z);
    return matrix;
  }

  public static translateToConfiguratorRotationMatrix(rotX: number, rotY: number, rotZ: number): Matrix4 {
    return MathUtils.euler2matrix(ExportToolUtils.translateToConfiguratorRotation(rotX, rotY, rotZ));
  }

  public static translateToConfiguratorRotation(rotX: number, rotY: number, rotZ: number): Vector3 {
    return new Vector3(-rotY * CommonConstants.COMMON_RADIANS_MULTIPLIER, rotZ * CommonConstants.COMMON_RADIANS_MULTIPLIER, -rotX * CommonConstants.COMMON_RADIANS_MULTIPLIER);
  }

  public static createColorMaterial(color: number): MeshPhongMaterial {
    return new MeshPhongMaterial({color: ExportToolUtils.translateToConfiguratorColor(color), opacity: 1, specular: 0});
  }

  public static translateToConfiguratorColor(color: number): Color {
    const r: number = (color >> 16 & 0xff) / 255;
    const g: number = (color >> 8 & 0xff) / 255;
    const b: number = (color & 0xff) / 255;
    return new Color(b, g, r);
  }

  public static createExtrudeMesh(points: Vector3[], mirror: Vector3, closed: boolean, material: MeshPhongMaterial): Mesh {
    if (closed && !MathUtils.equalsNumber3D(points[0], points[points.length - 1])) {
      points.push(points[0].clone());
    }
    let axis: string = ExportToolUtils.getExtrusionAxis(points, mirror);
    let depth: number = mirror[axis];
    if (depth < 0) {
      depth = Math.abs(depth);
      axis = '-' + axis;
    }
    if (depth === 0) {
      return ExportToolUtils.createFlatMesh(points, axis, material);
    }
    const mesh: Mesh = MeshUtils.drawShape(points, '', material, depth, axis);
    ExportToolUtils.checkUVMapping(mesh);
    return mesh;
  }

  public static getExtrusionAxis(points: Vector3[], mirror: Vector3): string {
    if (mirror.x != 0) {
      return 'x';
    }
    if (mirror.z != 0) {
      return 'z';
    }
    if (mirror.y != 0) {
      return 'y';
    }
    return ExportToolUtils.calculateExtrusionAxisByPoints(points);
  }

  public static calculateExtrusionAxisByPoints(points: Vector3[]): string {
    if (MathUtils.compare(points[0].x, points[1].x) === 0 && MathUtils.compare(points[2].x, points[1].x) === 0) {
      return 'x';
    } else if (MathUtils.compare(points[0].y, points[1].y) === 0 && MathUtils.compare(points[2].y, points[1].y) === 0) {
      return 'y';
    } else if (MathUtils.compare(points[0].z, points[1].z) === 0 && MathUtils.compare(points[2].z, points[1].z) === 0) {
      return 'z';
    } else {
      throw new Error('Extrusion could not be extruded on non flattern profile');
    }
  }

  public static createFlatMesh(points: Vector3[], axis: string, material: MeshPhongMaterial): Mesh {
    const shape = new Shape();
    let v = MeshUtils.getVertex2DByAxis(points[0], axis);
    shape.moveTo(v.x, v.y);
    for (let i = 1; i < points.length; i++) {
      v = MeshUtils.getVertex2DByAxis(points[i], axis);
      shape.lineTo(v.x, v.y);
    }
    if (shape.curves.length <= 0) {
      return null;
    }
    const geometry: ShapeGeometry = new ShapeGeometry(shape);
    const mesh: Mesh = new Mesh(geometry, material);
    ExportToolUtils.checkUVMapping(mesh);
    return mesh;
  }

  public static checkUVMapping(mesh: Mesh): void {
    if (!mesh) {
      return;
    }
    const geometry = mesh.geometry;
    if (!geometry.attributes.uv) {
      return;
    }
    const uvAttribute = geometry.attributes.uv as BufferAttribute;
    for (let i = 0; i < uvAttribute.count; i++) {
      let x = uvAttribute.getX(i);
      let y = uvAttribute.getY(i);
      x = x > 1 ? 1 : x < 0 ? 0 : x;
      y = y > 1 ? 1 : y < 0 ? 0 : y;
      uvAttribute.setXY(i, x, y);
    }
    uvAttribute.needsUpdate = true;
  }

  public static rotateConnectionByNormal(connection: Mesh, angle: number): void {
    if (!connection) {
      return;
    }
    const rectangle: Rectangle3D = MathUtils.calculateBoundingUVRectangle(connection.geometry);
    if (rectangle === null) {
      return;
    }
    const normal: Vector3 = MathUtils.getNormalFromFirstPoint(rectangle.point1, rectangle.point2, rectangle.point4);
    const meshTransform: Matrix4 = new Matrix4().makeRotationAxis(normal, angle);
    FaceUtils.transformMesh(connection, meshTransform);
  }

  public createTextMesh(textShape: WxText, drawingElement: WxDrawingElement, isConnection: boolean = false): Observable<Mesh> {
    const rectangle: Rectangle3D = Rectangle3D.fromArray(textShape.getPoints(isConnection));
    const mesh = ArticlesCreator.createPlaneForRectangle(rectangle);
    mesh.material = new MeshBasicMaterial({color: 0xFFFFFF});
    mesh.applyMatrix4(new Matrix4().makeRotationAxis(new Vector3(0, 0, 1), Math.PI));

    const textValue: SimpleTextValueVO = new SimpleTextValueVO();
    textValue.text = ExportToolUtils.formHtmlText(textShape, ExportToolUtils.translateToConfiguratorColor(drawingElement.rgb1));
    textValue.hAlignment = textShape.alignmentHorizontal;
    textValue.vAlignment = textShape.alignmentVertical;
    textValue.rotation = 90;

    const textureAreaUsageVO: TextureAreaUsageVO = new TextureAreaUsageVO();
    textureAreaUsageVO.textValue = textValue;
    textureAreaUsageVO.identity = MetaElementUsageIdentityVO.create(StringUtils.createUuid(), undefined);

    const labelTextureDescription: LabelTextureDescription = new LabelTextureDescription();
    labelTextureDescription.width = textShape.width;
    labelTextureDescription.height = textShape.length;
    labelTextureDescription.distanceToPreviousConnection = 0;
    labelTextureDescription.keepAspect = false;
    labelTextureDescription.textureAreaUsageVO = textureAreaUsageVO;

    return this.textureCacheManager.getLabelTextures([[labelTextureDescription]]).pipe(
      switchMap((labelTextures: LabelTextureDataVO[]) => {
        if (ArrayUtils.isNotEmpty(labelTextures)) {
          const labelTexture: LabelTextureDataVO = labelTextures[0];
          return TextureUtils.createImage(labelTexture.textureData, 'png', String(textShape.textId));
        } else {
          return of(mesh);
        }
      }),
      switchMap((image: HTMLImageElement) => {
        ExportToolUtils.applyImageToMeshMaterial(image, mesh);
        return of(mesh);
      })
    );
  }

  private static formHtmlText(text: WxText, color: Color): string {
    const r: string = NumberUtils.integerToHex(color.r * 255);
    const g: string = NumberUtils.integerToHex(color.g * 255);
    const b: string = NumberUtils.integerToHex(color.b * 255);
    const hexColor: string = `#${r + g + b}`;

    if (text === null) {
      return null;
    }
    return HtmlUtils.createLegacyFontParagraph(text.text, {
      fontSize: text.fontSize,
      color: hexColor,
      bold: text.fontBold,
      italic: text.fontItalic,
      underline: text.fontUnderline,
    });
  }

  public static applyImageToMeshMaterial(image: HTMLImageElement, mesh: Mesh): void {
    if (image && mesh) {
      const texture: Texture = new Texture();
      texture.image = image;
      texture.wrapS = texture.wrapT = RepeatWrapping;
      texture.needsUpdate = true;
      if (IsThreeJsType.meshBasicMaterial(mesh.material) || IsThreeJsType.meshPhongMaterial(mesh.material)) {
        mesh.material.map = texture;
        mesh.material.needsUpdate = true;
      }
    }
  }
}
